// @ts-nocheck
import React from "react";
import { motion, MotionValue } from "framer-motion";
import { FaGithub, FaLinkedin, FaEnvelope } from "react-icons/fa";
import { useTypingAnimation } from "../../hooks/useTypingAnimation";
import {
  HERO_NAME,
  TYPING_SPEED,
  QUICK_STATS,
  SOCIAL_LINKS,
} from "./Hero.constants";

interface HeroProps {
  backgroundY: MotionValue<string>;
}

const Hero: React.FC<HeroProps> = ({ backgroundY }) => {
  const { displayedText, isComplete } = useTypingAnimation(
    HERO_NAME,
    TYPING_SPEED,
  );

  const getSocialIcon = (label: string) => {
    switch (label) {
      case "GitHub":
        return <FaGithub />;
      case "LinkedIn":
        return <FaLinkedin />;
      case "Email":
        return <FaEnvelope />;
      default:
        return null;
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center px-6 pb-24">
      <motion.div
        style={{ y: backgroundY }}
        className="absolute inset-0 opacity-20"
      >
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl" />
      </motion.div>

      <div className="relative z-10 text-center max-w-4xl mx-auto">
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", duration: 1.5 }}
          className="mb-8"
        >
          <div className="w-40 h-40 mx-auto rounded-full gradient-border glass flex items-center justify-center floating">
            <span className="text-6xl font-bold">DV</span>
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-5xl md:text-7xl font-bold mb-4"
        >
          <span className="gradient-text">{displayedText}</span>
          {!isComplete && <span className="typing-cursor" />}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2 }}
          className="space-y-4"
        >
          <p className="text-xl md:text-2xl text-slate-300">
            Senior Software Engineer | React | TypeScript | Team Lead
          </p>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            17+ years of experience building web applications. Former Department
            Lead managing 100+ developers. Currently at Proprio Cloud Solutions,
            building React applications for NetSuite.
          </p>

          <div className="flex flex-wrap justify-center gap-6 mt-6">
            {QUICK_STATS.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className={`text-3xl font-bold ${stat.color}`}>
                  {stat.value}
                </p>
                <p className="text-sm text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>

          <div className="flex justify-center gap-6 mt-8">
            {SOCIAL_LINKS.map((link) => (
              <motion.a
                key={link.label}
                href={link.href}
                target={link.label !== "Email" ? "_blank" : undefined}
                rel={link.label !== "Email" ? "noopener noreferrer" : undefined}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: link.delay }}
                whileHover={{ scale: 1.2, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
                className="w-12 h-12 rounded-full glass flex items-center justify-center text-xl hover:text-blue-400 transition-colors"
              >
                {getSocialIcon(link.label)}
              </motion.a>
            ))}
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="text-slate-400 text-center"
        >
          <span className="text-sm">Scroll Down</span>
          <div className="mt-2 w-6 h-10 border-2 border-slate-400 rounded-full mx-auto flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1.5 h-3 bg-slate-400 rounded-full mt-2"
            />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
